import type { LeadAnalysisResponse } from 'app/apiService/leads/leadAnalysisApiService';
import type { Segment } from '../types';

/** Programme day when a segment starts loading (matches API nurturingPlanSegmentUtils). */
const startDayForSegmentIndex = (index: number): number => {
  if (index < 2) return 1;
  if (index === 2) return 7;
  if (index === 3) return 14;
  return 21 + (index - 4) * 7;
};

const num = (value: unknown, fallback: number): number => {
  if (typeof value === 'number' && !Number.isNaN(value)) return value;
  if (typeof value === 'string') {
    const asNum = Number(value.trim());
    return Number.isNaN(asNum) ? fallback : asNum;
  }
  return fallback;
};

export interface LookBackDateRange {
  startDate: string | null;
  endDate: string | null;
}

export interface LeadAnalysisPlanFields {
  total: number;
  monthly: number;
  look: number;
  look_back_start_date: string | null;
  look_back_end_date: string | null;
  segments: Segment[];
}

/** Map lead analysis metrics into left-panel CRM pool + segment state. */
export function mapLeadAnalysisToPlanFields(
  analysis: LeadAnalysisResponse,
  range: LookBackDateRange,
): LeadAnalysisPlanFields {
  const total = Math.max(0, num(analysis.total_available_leads?.value, 0));
  const monthly = Math.max(0, num(analysis.average_leads_per_month?.value, 0));
  const look = Math.max(0, num(analysis.look_back_months?.value, 0));

  const rows = (analysis.group_by_segment?.value ?? [])
    .map((row) => ({
      name: String(row.segment || '').trim(),
      count: num(row.count, 0),
      pct: num(row.percentage, 0),
    }))
    .filter((row) => row.name && row.count > 0)
    .sort((a, b) => b.count - a.count);

  const segments: Segment[] = rows.map((row, index) => ({
    id: index + 1,
    name: row.name,
    pct: row.pct,
    startDay: startDayForSegmentIndex(index),
    order: index + 1,
  }));

  return {
    total,
    monthly,
    look: look > 0 ? look : 12,
    look_back_start_date: analysis.look_back_start_date || range.startDate,
    look_back_end_date:
      analysis.look_back_end_date || analysis.look_back_date || range.endDate,
    segments,
  };
}
